import type { ReactNode } from 'react';

/**
 * A handful of stroke icons, drawn inline so they work offline and never
 * stand alone: every one is paired with a word (Section 9).
 */

const paths: Record<string, ReactNode> = {
  home: <path d="M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10" />,
  broom: <path d="M14 3l7 7M13 8l-9 9 3 3 9-9M6 15l3 3" />,
  wrench: <path d="M14 6a4 4 0 005 5l-9 9a2 2 0 01-3-3l9-9a4 4 0 01-2-2z" />,
  book: <path d="M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2zM4 19V5M9 7h6" />,
  dots: <path d="M5 12h.01M12 12h.01M19 12h.01" />,
  camera: <path d="M4 8h3l2-3h6l2 3h3v11H4zM12 17a4 4 0 100-8 4 4 0 000 8z" />,
  mic: <path d="M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3zM6 11a6 6 0 0012 0M12 17v4" />,
  check: <path d="M5 12l5 5 9-10" />,
  alert: <path d="M12 3l10 18H2zM12 10v5M12 18h.01" />,
  chat: <path d="M4 5h16v11H9l-5 4z" />,
  translate: <path d="M4 5h9M8 3v2M6 5c1 4 4 7 8 8M12 5c-1 4-4 7-8 8M14 20l4-10 4 10M15.5 17h5" />,
  star: <path d="M12 3l3 6 6 1-4.5 4.5 1 6.5-5.5-3-5.5 3 1-6.5L3 10l6-1z" />,
  users: <path d="M9 11a3 3 0 100-6 3 3 0 000 6zM3 20a6 6 0 0112 0M16 5a3 3 0 010 6M18 14a6 6 0 013 6" />,
  phone: <path d="M8 2h8a1 1 0 011 1v18a1 1 0 01-1 1H8a1 1 0 01-1-1V3a1 1 0 011-1zM11 19h2" />,
  play: <path d="M7 4l13 8-13 8z" />,
  send: <path d="M3 11l18-8-8 18-2-8z" />,
  sun: <path d="M12 8a4 4 0 100 8 4 4 0 000-8zM12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5" />,
  boat: <path d="M3 15h18l-3 5H6zM12 3v10M12 4l6 8h-6" />,
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  battery: <path d="M2 9a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2zM22 11v2M6 10v4M10 10v4" />,
  drop: <path d="M12 3s6 6.5 6 11a6 6 0 01-12 0c0-4.5 6-11 6-11z" />,
  snow: <path d="M12 2v20M4.5 6.5l15 11M19.5 6.5l-15 11" />,
  wifi: <path d="M2 9a15 15 0 0120 0M5 12.5a10 10 0 0114 0M8.5 16a5 5 0 017 0M12 19.5h.01" />,
  eye: <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 100-6 3 3 0 000 6z" />,
  eyeOff: <path d="M3 3l18 18M10.6 6.1A9.8 9.8 0 0112 6c6 0 10 6 10 6a17 17 0 01-3.2 3.9M6.6 7.6A17 17 0 002 12s4 7 10 7a9.6 9.6 0 004.3-1M9.9 9.9a3 3 0 004.2 4.2" />,
  refresh: <path d="M20 11a8 8 0 00-14-4L4 9M4 4v5h5M4 13a8 8 0 0014 4l2-2M20 20v-5h-5" />,
  bed: <path d="M3 18v-5a2 2 0 012-2h14a2 2 0 012 2v5M3 18v2M21 18v2M3 13V8M7 11V9a1 1 0 011-1h3a1 1 0 011 1v2" />,
};

export type IconName = keyof typeof paths;

export function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      {paths[name]}
    </svg>
  );
}
